import { Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import {
  catchError,
  firstValueFrom,
  from,
  lastValueFrom,
  map,
  Observable,
  of,
} from "rxjs";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class EmailService {
  private graphApiUrl = "https://graph.microsoft.com/v1.0";
  emails: any;
  isLogged: boolean = false;
  isPolicyAgreed: boolean = false;
  isFullScreen: boolean = false;
  isSentTabSelected: boolean = false;

  constructor(private msalService: MsalService, private http: HttpClient) {
    msalService.initialize();
  }

  // Login method that also sets the active account after a successful login
  login(): Observable<"success" | "failed"> {
    const allAccounts = this.msalService.instance.getAllAccounts() || [];
    const storedUsername = sessionStorage.getItem("activeUser");

    if (storedUsername && allAccounts.length > 0) {
      const matchedAccount = allAccounts.find(
        (acc) => acc.username === storedUsername
      );
      if (matchedAccount) {
        this.msalService.instance.setActiveAccount(matchedAccount);
        return of<"success">("success");
      }
    }

    const activeAccount = this.msalService.instance.getActiveAccount();

    if (activeAccount || allAccounts.length > 0) {
      const accountToSet = activeAccount || allAccounts[0];
      this.msalService.instance.setActiveAccount(accountToSet);
      sessionStorage.setItem("activeUser", accountToSet.username);
      return of<"success">("success");
    }

    // Login Popup
    const redirectUri =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : environment.redirectUri;

    return from(
      this.msalService.loginPopup({
        scopes: ["Mail.Send", "Mail.Read", "Calendars.ReadWrite"],
        redirectUri,
        // prompt: 'consent'
      })
    ).pipe(
      map(() => {
        const accounts = this.msalService.instance.getAllAccounts() || [];
        if (accounts.length > 0) {
          this.msalService.instance.setActiveAccount(accounts[0]);
          sessionStorage.setItem("activeUser", accounts[0].username);
          return "success" as const;
        }
        return "failed" as const;
      }),
      catchError((error) => {
        console.error("Login failed:", error);
        return of<"failed">("failed");
      })
    );
  }

  getEmailById(mailId: string): Promise<any> {
    const emailUrl = `https://graph.microsoft.com/v1.0/me/messages/${mailId}`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http.get(emailUrl, { headers }).toPromise();
  }
  getEmailTemplates(): Promise<any> {
    const emailTemplatesUrl =
      environment.apiBaseUrl + "/api/EmailTemplate/get-email-templates";
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });
    return this.http.get(emailTemplatesUrl, { headers }).toPromise();
  }

  getExtractEmailTemplates(emailTemplateKey: number): Promise<any> {
    const emailTemplatesUrl =
      environment.apiBaseUrl + "/api/EmailTemplate/get-extract-email-templates";
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });
    const body = {
      brand_key: emailTemplateKey,
    };
    return this.http.post(emailTemplatesUrl, body, { headers }).toPromise();
  }

  getEditEmailTemplate(emailTemplateKey: number): Promise<any> {
    const editTemplateUrl =
      environment.apiBaseUrl + "/api/EmailTemplate/edit-email-template";
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });
    const body = {
      email_template_key: emailTemplateKey,
    };
    return this.http.post(editTemplateUrl, body, { headers }).toPromise();
  }

  testSqlQuery(payload: {
    sqlQuery: string;
    secretKey?: string;
  }): Promise<any> {
    const testSqlUrl = environment.apiBaseUrl + "/api/EmailTemplate/test-sql";
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });
    return this.http.post(testSqlUrl, payload, { headers }).toPromise();
  }

  deleteTemplates(templateKey: number): Promise<any> {
    const deleteTemplateUrl =
      environment.apiBaseUrl + "/api/EmailTemplate/delete-email-template";
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });
    // Pass the templateKey in the body
    const body = {
      email_template_key: templateKey,
    };
    return this.http.post(deleteTemplateUrl, body, { headers }).toPromise();
  }

  getTemplateAttachments(emailTemplateKey: number): Promise<any> {
    const emailTemplatesUrl = `${environment.apiBaseUrl}/api/EmailTemplate/get-email-data`;
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    // Pass the emailTemplateKey in the body
    const body = {
      email_template_key: emailTemplateKey,
    };

    return this.http.post(emailTemplatesUrl, body, { headers }).toPromise();
  }

  createNewTemplates(formData: FormData): Promise<any> {
    const createTemplateUrl =
      environment.apiBaseUrl + "/api/EmailTemplate/create-email-templates";
    return this.http.post(createTemplateUrl, formData).toPromise();
  }

  editTemplates(formData: FormData): Promise<any> {
    const createTemplateUrl =
      environment.apiBaseUrl + "/api/EmailTemplate/create-email-templates";
    return this.http.post(createTemplateUrl, formData).toPromise();
  }

  async getSentEmails(): Promise<any[]> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("User is not logged in.");

    try {
      const authResult = await lastValueFrom(
        this.msalService.acquireTokenSilent({ account, scopes: ["Mail.Read"] })
      );

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: `Bearer ${authResult.accessToken}`,
      });

      const url = `${this.graphApiUrl}/me/mailFolders/sentitems/messages?$top=50&$select=id,subject,bodyPreview,from,toRecipients,receivedDateTime`;

      const response: any = await lastValueFrom(
        this.http.get(url, { headers })
      );
      return response.value || [];
    } catch (error) {
      console.error("Error fetching sent emails:", error);
      return [];
    }
  }

  async getEmails(): Promise<any[]> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("User is not logged in.");

    try {
      const authResult = await lastValueFrom(
        this.msalService.acquireTokenSilent({
          account,
          scopes: ["Mail.Read"],
        })
      );

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: `Bearer ${authResult.accessToken}`,
      });

      const url = `${this.graphApiUrl}/me/mailFolders/inbox/messages?$top=50&$select=id,subject,bodyPreview,from,receivedDateTime`;

      const response = await lastValueFrom(
        this.http.get<{ value: any[] }>(url, { headers })
      );

      return response.value || [];
    } catch (error) {
      console.error("Error fetching inbox emails:", error);
      return [];
    }
  }

  async sendEmail(
    to: string,
    subject: string,
    body: string,
    attachments: File[] = [],
    cc: string = ""
  ): Promise<any> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("User is not logged in.");

    try {
      const authResult = await lastValueFrom(
        this.msalService.acquireTokenSilent({
          account,
          scopes: ["Mail.Send"],
        })
      );

      const headers = new HttpHeaders({
        Authorization: `Bearer ${authResult.accessToken}`,
        "Content-Type": "application/json",
      });

      const attachmentData = await Promise.all(
        attachments.map(async (file) => ({
          "@odata.type": "#microsoft.graph.fileAttachment",
          name: file.name,
          contentType: file.type,
          contentBytes: await this.readFileAsBase64(file),
        }))
      );

      const detectContentType = (content: string) =>
        /<\/?[a-z][\s\S]*>/i.test(content) ? "HTML" : "Text";

      const toRecipientsArray = to
        .split(",")
        .map((e) => ({
          emailAddress: { address: e.trim() },
        }))
        .filter((e) => e.emailAddress.address);

      const ccRecipientsArray = cc
        .split(",")
        .map((e) => ({
          emailAddress: { address: e.trim() },
        }))
        .filter((e) => e.emailAddress.address);

      const emailData: any = {
        message: {
          subject,
          body: { contentType: detectContentType(body), content: body },
          toRecipients: toRecipientsArray,
          ccRecipients: ccRecipientsArray,
          attachments: attachmentData,
        },
        saveToSentItems: "true",
      };

      return await lastValueFrom(
        this.http.post(`${this.graphApiUrl}/me/sendMail`, emailData, {
          headers,
        })
      );
    } catch (error) {
      console.error("Error sending email:", error);
      throw error;
    }
  }

  async getAttachments(emailId: string, accessToken: any): Promise<any[]> {
    const attachmentsUrl = `https://graph.microsoft.com/v1.0/me/messages/${emailId}/attachments`;

    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    });

    try {
      const response: any = await lastValueFrom(
        this.http.get(attachmentsUrl, { headers })
      );

      return await Promise.all(
        response.value.map(async (attachment: any) => {
          const contentBytes = attachment.isInline
            ? null
            : await this.getAttachmentContent(emailId, attachment.id);
          return {
            id: attachment.id,
            name: attachment.name,
            contentType: attachment.contentType,
            contentId: attachment.contentId,
            size: attachment.size,
            isInline: attachment.isInline,
            contentBytes: attachment.contentBytes,
          };
        })
      );
    } catch (error: any) {
      console.error(
        `Error fetching attachments for email ${emailId}:`,
        error.message || error
      );
      return [];
    }
  }

  async getAttachmentContent(
    emailId: string,
    attachmentId: string
  ): Promise<string> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("No active account");

    const authResult = await lastValueFrom(
      this.msalService.acquireTokenSilent({
        account,
        scopes: ["Mail.Read"],
      })
    );

    const accessToken = authResult.accessToken;
    const attachmentUrl = `https://graph.microsoft.com/v1.0/me/messages/${emailId}/attachments/${attachmentId}`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    });

    try {
      const response: any = await lastValueFrom(
        this.http.get(attachmentUrl, { headers })
      );
      return response.contentBytes;
    } catch (error) {
      console.error("Error fetching attachment content:", error);
      throw error;
    }
  }

  async replyToEmail(
    messageId: string,
    payload: { body: string; attachments: File[] }
  ): Promise<any> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("User is not logged in.");

    const authResult = await lastValueFrom(
      this.msalService.acquireTokenSilent({ account, scopes: ["Mail.Send"] })
    );

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${authResult.accessToken}`,
    });

    const attachmentData = await Promise.all(
      payload.attachments.map(async (file) => ({
        "@odata.type": "#microsoft.graph.fileAttachment",
        name: file.name,
        contentType: file.type,
        contentBytes: await this.convertFileToBase64(file),
      }))
    );

    const body = {
      comment: payload.body,
      message: {
        attachments: attachmentData,
      },
    };

    return lastValueFrom(
      this.http.post(
        `${this.graphApiUrl}/me/messages/${messageId}/replyAll`,
        body,
        { headers }
      )
    );
  }

  async forwardEmail(
    messageId: string,
    payload: {
      to: string[];
      cc?: string[];
      subject?: string;
      body: string;
      attachments: File[];
    }
  ): Promise<any> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("User is not logged in.");

    const authResult = await lastValueFrom(
      this.msalService.acquireTokenSilent({ account, scopes: ["Mail.Send"] })
    );

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${authResult.accessToken}`,
    });

    const attachmentData = await Promise.all(
      payload.attachments.map(async (file) => ({
        "@odata.type": "#microsoft.graph.fileAttachment",
        name: file.name,
        contentType: file.type,
        contentBytes: await this.convertFileToBase64(file),
      }))
    );

    const toRecipients = (payload.to || []).map((email) => ({
      emailAddress: { address: email },
    }));
    const ccRecipients = (payload.cc || []).map((email) => ({
      emailAddress: { address: email },
    }));

    const body: any = {
      toRecipients,
      comment: payload.body,
      message: {
        subject: payload.subject,
        attachments: attachmentData,
      },
    };

    if (ccRecipients.length > 0) {
      body.ccRecipients = ccRecipients;
    }

    return lastValueFrom(
      this.http.post(
        `${this.graphApiUrl}/me/messages/${messageId}/forward`,
        body,
        { headers }
      )
    );
  }

  private convertFileToBase64(file: File): string {
    const reader = new FileReader();
    let base64String = "";

    reader.onload = () => {
      base64String = (reader.result as string).split(",")[1];
    };

    reader.readAsDataURL(file);
    return base64String;
  }

  async getEmailsbyId(mailId: string): Promise<any> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("User is not logged in.");

    try {
      const authResult = await lastValueFrom(
        this.msalService.acquireTokenSilent({
          account,
          scopes: ["Mail.Read"],
        })
      );

      const accessToken = authResult.accessToken;
      if (!accessToken) throw new Error("No access token available");

      const emailUrl = `https://graph.microsoft.com/v1.0/me/messages/${mailId}`;
      const headers = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      });

      const email: any = await lastValueFrom(
        this.http.get(emailUrl, { headers })
      );
      const attachments = await this.getAttachments(mailId, accessToken);

      return {
        id: email.id,
        subject: email.subject,
        body: email.body,
        from: email.from?.emailAddress?.name || "Unknown Sender",
        fromEmail: email.from?.emailAddress?.address || "",
        receivedDateTime: email.receivedDateTime,
        conversationId: email.conversationId,
        attachments,
        to:
          email.toRecipients
            ?.map((r: any) => r.emailAddress?.name)
            .join(", ") || "Unknown Recipient",
        toEmail:
          email.toRecipients
            ?.map((r: any) => r.emailAddress?.address)
            .join(", ") || "Unknown Recipient",
      };
    } catch (error) {
      console.error(`Error fetching email with ID ${mailId}:`, error);
      throw error;
    }
  }

  async getConversationMessagesWithAttachments(
    conversationId: string
  ): Promise<any[]> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("User is not logged in.");

    try {
      const authResult = await lastValueFrom(
        this.msalService.acquireTokenSilent({
          account,
          scopes: ["Mail.Read"],
        })
      );

      const accessToken = authResult.accessToken;
      if (!accessToken) throw new Error("Access token not available");

      const url = `https://graph.microsoft.com/v1.0/me/messages?$filter=conversationId eq '${conversationId}'&$select=id,subject,body,bodyPreview,from,receivedDateTime`;

      const headers = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      });

      const response: any = await lastValueFrom(
        this.http.get(url, { headers })
      );
      // console.log("Raw API Response:", response.value); // Optional for debugging

      const messagesWithAttachments = await Promise.all(
        response.value.map(async (message: any) => {
          const attachments = await this.getAttachments(
            message.id,
            accessToken
          );

          // Replace inline image CIDs in body with base64 content
          let bodyContent = message.body?.content || "";
          attachments
            .filter((att) => att.isInline && att.contentId && att.contentBytes)
            .forEach((att) => {
              const cidRef = `cid:${att.contentId}`;
              const base64Src = `data:${att.contentType};base64,${att.contentBytes}`;
              bodyContent = bodyContent.replaceAll(cidRef, base64Src);
            });

          return {
            ...message,
            body: {
              ...message.body,
              content: bodyContent,
            },
            attachments: attachments,
          };
        })
      );

      return messagesWithAttachments;
    } catch (error: any) {
      console.error(
        "Error fetching conversation messages with attachments:",
        error.message || error
      );
      throw error;
    }
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = (reader.result as string).split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }
  async createGraphCalendarEvent(event: any): Promise<any> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("User is not logged in.");

    const authResult = await lastValueFrom(
      this.msalService.acquireTokenSilent({
        account,
        scopes: ["Calendars.ReadWrite"],
      })
    );

    const accessToken = authResult.accessToken;
    if (!accessToken) throw new Error("No access token available");

    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    });

    return lastValueFrom(
      this.http.post("https://graph.microsoft.com/v1.0/me/events", event, {
        headers,
      })
    );
  }

  async updateGraphCalendarEvent(
    graphEventId: string,
    event: any
  ): Promise<any> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("User is not logged in.");

    const authResult = await lastValueFrom(
      this.msalService.acquireTokenSilent({
        account,
        scopes: ["Calendars.ReadWrite"],
      })
    );

    const accessToken = authResult.accessToken;
    if (!accessToken) throw new Error("No access token available");

    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    });

    return lastValueFrom(
      this.http.patch(
        `https://graph.microsoft.com/v1.0/me/events/${graphEventId}`,
        event,
        { headers }
      )
    );
  }

  async deleteGraphCalendarEvent(eventId: string): Promise<boolean> {
    try {
      const account = this.msalService.instance.getActiveAccount();
      if (!account) throw new Error("User is not logged in.");

      const authResult = await lastValueFrom(
        this.msalService.acquireTokenSilent({
          account,
          scopes: ["Calendars.ReadWrite"],
        })
      );

      const accessToken = authResult.accessToken;
      if (!accessToken) throw new Error("No access token available");

      const headers = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      });

      await lastValueFrom(
        this.http.delete(
          `https://graph.microsoft.com/v1.0/me/events/${eventId}`,
          { headers }
        )
      );

      return true; // deletion successful
    } catch (error) {
      console.error("Error deleting graph calendar event:", error);
      throw error;
    }
  }
  // email.service.ts

  async createTeamsMeeting(
    subject: string,
    startDateTime: string,
    endDateTime: string
  ): Promise<any> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) throw new Error("User is not logged in.");

    try {
      // ✅ Same way as sendEmail
      const authResult = await lastValueFrom(
        this.msalService.acquireTokenSilent({
          account,
          scopes: ["OnlineMeetings.ReadWrite"],
          //  prompt: 'consent'
        })
      );

      const headers = new HttpHeaders({
        Authorization: `Bearer ${authResult.accessToken}`,
        "Content-Type": "application/json",
      });

      const meetingData = {
        subject,
        startDateTime,
        endDateTime,
        participants: {
          organizer: {
            identity: {
              user: {
                id: account.localAccountId, // you can also use account.username/email if required
              },
            },
          },
        },
      };

      return await lastValueFrom(
        this.http.post(`${this.graphApiUrl}/me/onlineMeetings`, meetingData, {
          headers,
        })
      );
    } catch (error) {
      console.error("Error creating Teams meeting:", error);
      throw error;
    }
  }
}
